import { Eye, Users } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/shared";
import { projectService } from "@/lib/services";
import type { ProjectHub as ProjectHubType } from "@/types";

// ---------------------------------------------------------------------------
// Settings Tab (owner only)
// ---------------------------------------------------------------------------

export function SettingsTab({
  hub,
  projectId,
  onRefresh,
}: Readonly<{
  hub: ProjectHubType;
  projectId: string;
  onRefresh: () => Promise<void> | void;
}>) {
  const { showToast } = useToast();
  const [updatingVisibility, setUpdatingVisibility] = useState(false);

  const handleVisibilityChange = async (visibility: "private" | "internal") => {
    if (visibility === hub.visibility || updatingVisibility) return;
    try {
      setUpdatingVisibility(true);
      await projectService.update(projectId, { visibility });
      showToast(`Project is now ${visibility}`, "success");
      await onRefresh();
    } catch (err) {
      console.error("Failed to update project visibility:", err);
      showToast("Failed to update visibility", "error");
    } finally {
      setUpdatingVisibility(false);
    }
  };

  const visibilityOptions: {
    value: "private" | "internal";
    label: string;
    description: string;
    icon: typeof Eye;
  }[] = [
    { value: "private", label: "Private", description: "Only members can see this project.", icon: Eye },
    {
      value: "internal",
      label: "Internal",
      description: "All authenticated users can view this project (read-only unless also a member).",
      icon: Users,
    },
  ];

  return (
    <div className="p-6 max-w-2xl space-y-6">
      <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
        <h3 className="text-lg font-semibold text-content-primary mb-2">Project Info</h3>
        <dl className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-content-tertiary">Status</dt>
            <dd className="text-content-primary capitalize">{hub.status}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-content-tertiary">Created</dt>
            <dd className="text-content-primary">{new Date(hub.createdAt).toLocaleDateString()}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-content-tertiary">Your role</dt>
            <dd className="text-content-primary capitalize">{hub.currentUserRole?.replace("_", " ") || "N/A"}</dd>
          </div>
          {hub.defaultDataSource && (
            <div className="flex items-center justify-between">
              <dt className="text-content-tertiary">Knowledge Base</dt>
              <dd className="text-content-primary">{hub.defaultDataSource.fileCount} files indexed</dd>
            </div>
          )}
        </dl>
      </div>

      <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
        <h3 className="text-lg font-semibold text-content-primary mb-1">Visibility</h3>
        <p className="text-sm text-content-tertiary mb-4">Control who can see this project.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {visibilityOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = hub.visibility === option.value;
            return (
              <button
                key={option.value}
                onClick={() => handleVisibilityChange(option.value)}
                disabled={updatingVisibility}
                className={`flex items-start gap-3 p-4 rounded-lg border transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed ${
                  isSelected
                    ? "border-brand-primary bg-brand-primary/10"
                    : "border-border-secondary hover:border-border-hover bg-surface-secondary"
                }`}
              >
                <Icon
                  className={`h-5 w-5 shrink-0 mt-0.5 ${isSelected ? "text-brand-primary" : "text-content-tertiary"}`}
                />
                <div>
                  <p className={`text-sm font-medium ${isSelected ? "text-brand-primary" : "text-content-primary"}`}>
                    {option.label}
                  </p>
                  <p className="text-xs text-content-quaternary mt-0.5">{option.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
